import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../services/api';

/**
 * CsvImportModal
 *
 * Bulk-uploads a CSV into the brain. Five visual states drive the UI:
 *
 *   1. 'rules'    — explainer + drag-drop / file picker.
 *   2. 'preview'  — file picked; show headers + Import/Ignore toggles
 *                   alongside sample values, surface any sanity warnings.
 *   3. 'upload'   — multipart upload in flight, network progress bar.
 *   4. 'progress' — server has the file; poll for processed/succeeded/failed.
 *   5. 'summary'  — terminal state (completed / canceled / failed); show
 *                   counts and a "Download rejected rows" link if any.
 *
 * Browser-side header preview pulls only the first 64 KB of the file
 * with FileReader.slice() so a 95 MB CSV doesn't get loaded into RAM
 * twice. The full upload only kicks off after the user confirms the
 * mapping.
 *
 * Polling cadence is 1.5s — cheap (one row read on the server) and
 * keeps the UI feeling live without being a hammer.
 */

interface Props {
  open: boolean;
  dataBodyId: string;
  dataBodyName: string;
  onClose: () => void;
}

type Stage = 'rules' | 'preview' | 'upload' | 'progress' | 'summary';

interface HeaderPreview {
  headers: string[];                                    // row 1, exactly as-is
  sampleRows: string[][];                               // up to 3 data rows
  warnings: { type: 'info' | 'warning'; text: string }[];
  rowColumnMismatchPct: number;                         // 0..1
}

const HEADER_PREVIEW_BYTES = 64 * 1024;

// Cheap, dependency-free CSV-ish parser that only needs to handle
// the first 64 KB. RFC4180 quoting, comma delimiter. Good enough
// for the preview-only use case; the real parser on the server
// handles all the gnarly edge cases.
function parsePreviewCsv(text: string): string[][] {
  const rows: string[][] = [];
  let cur: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; }
        else { inQuotes = false; }
      } else {
        cell += c;
      }
    } else {
      if (c === '"' && cell === '') {
        inQuotes = true;
      } else if (c === ',') {
        cur.push(cell); cell = '';
      } else if (c === '\r') {
        // skip; \n will close
      } else if (c === '\n') {
        cur.push(cell); cell = '';
        rows.push(cur); cur = [];
      } else {
        cell += c;
      }
    }
  }
  // Drop the last partial row (we likely truncated mid-row at 64KB).
  if (cell !== '' || cur.length > 0) {
    // ignored
  }
  return rows;
}

function looksLikeData(s: string): { reason: string } | null {
  const t = s.trim();
  if (!t) {return null;}
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) {return { reason: 'looks like an email address' };}
  if (/^\+?\d[\d\s()-]{6,}$/.test(t)) {return { reason: 'looks like a phone number' };}
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {return { reason: 'looks like a date' };}
  if (/^[0-9]+(\.[0-9]+)?$/.test(t) && t.length > 2) {return { reason: 'looks like a number' };}
  return null;
}

function buildHeaderPreview(text: string): HeaderPreview | null {
  // Strip UTF-8 BOM
  if (text.charCodeAt(0) === 0xFEFF) {text = text.slice(1);}

  const rows = parsePreviewCsv(text);
  if (rows.length === 0) {return null;}

  const headers = rows[0];
  const sampleRows = rows.slice(1, 4);
  const warnings: { type: 'info' | 'warning'; text: string }[] = [];

  // Header sanity heuristics
  const nonEmpty = headers.filter(h => h.trim() !== '');
  if (nonEmpty.length === 0) {
    warnings.push({ type: 'warning', text: 'Row 1 is empty — no headers detected.' });
  }

  // Empty cells in the header row
  const emptyHeaderIdx = headers
    .map((h, i) => (h.trim() === '' ? i + 1 : -1))
    .filter(i => i > 0);
  if (emptyHeaderIdx.length > 0) {
    warnings.push({
      type: 'warning',
      text: `Header row has empty cell(s) at column ${emptyHeaderIdx.join(', ')}. These columns must be Ignored or named before import.`,
    });
  }

  // Headers that look like data (single-column case in particular)
  const dataLooking = headers
    .map((h, i) => ({ i: i + 1, h, hit: looksLikeData(h) }))
    .filter(x => x.hit);
  if (dataLooking.length > 0) {
    const head = dataLooking.slice(0, 2)
      .map(x => `"${x.h}" (${x.hit!.reason})`)
      .join(', ');
    warnings.push({
      type: 'warning',
      text: `Some header cells look like data, not headers: ${head}. The file may be missing a header row.`,
    });
  }

  // Duplicate headers
  const seen = new Map<string, number>();
  const dupes: string[] = [];
  for (const h of headers) {
    const key = h.trim();
    if (!key) {continue;}
    seen.set(key, (seen.get(key) || 0) + 1);
  }
  seen.forEach((n, k) => { if (n > 1) {dupes.push(`"${k}" (×${n})`);} });
  if (dupes.length > 0) {
    warnings.push({
      type: 'warning',
      text: `Duplicate header(s): ${dupes.join(', ')}. Only the last occurrence of each will survive import.`,
    });
  }

  // Headers >100 chars
  const tooLong = headers.filter(h => h.length > 100);
  if (tooLong.length > 0) {
    warnings.push({
      type: 'warning',
      text: `${tooLong.length} header${tooLong.length === 1 ? ' is' : 's are'} longer than 100 characters; may not be a real header row.`,
    });
  }

  // Column count consistency vs the sample rows
  let mismatched = 0;
  for (const r of sampleRows) {
    if (r.length !== headers.length) {mismatched++;}
  }
  const rowColumnMismatchPct = sampleRows.length > 0 ? mismatched / sampleRows.length : 0;
  if (rowColumnMismatchPct >= 0.5 && sampleRows.length >= 2) {
    warnings.push({
      type: 'warning',
      text: `Sample data rows have a different number of columns than the header. The file may be malformed.`,
    });
  }

  return { headers, sampleRows, warnings, rowColumnMismatchPct };
}

function fmtBytes(n: number): string {
  if (n < 1024) {return `${n} B`;}
  if (n < 1024 * 1024) {return `${(n / 1024).toFixed(1)} KB`;}
  if (n < 1024 * 1024 * 1024) {return `${(n / 1024 / 1024).toFixed(1)} MB`;}
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtNum(n: number): string {
  return n.toLocaleString();
}

export default function CsvImportModal({ open, dataBodyId, dataBodyName, onClose }: Props) {
  const [stage, setStage] = useState<Stage>('rules');
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<HeaderPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, 'import' | 'ignore'>>({});
  const [limits, setLimits] = useState<api.CsvImportLimits | null>(null);

  const [uploadPct, setUploadPct] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<api.CsvImportStatus | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset everything when the modal closes/reopens
  useEffect(() => {
    if (!open) {
      setStage('rules');
      setError(null);
      setFile(null);
      setPreview(null);
      setMapping({});
      setUploadPct(0);
      setJobId(null);
      setStatus(null);
      setCanceling(false);
      if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
    }
  }, [open]);

  // Pull limits once on first open
  useEffect(() => {
    if (open && !limits) {
      api.getCsvImportLimits().then(setLimits).catch(() => {
        setLimits({ maxUploadBytes: 95 * 1024 * 1024, blankRowGapStop: 50 });
      });
    }
  }, [open, limits]);

  function handleFilePicked(picked: File) {
    setError(null);
    if (!/\.csv$/i.test(picked.name)) {
      setError(`"${picked.name}" is not a .csv file. Only .csv files are accepted.`);
      return;
    }
    if (limits && picked.size > limits.maxUploadBytes) {
      setError(`File is ${fmtBytes(picked.size)} — exceeds the ${fmtBytes(limits.maxUploadBytes)} limit.`);
      return;
    }
    if (picked.size === 0) {
      setError('File is empty.');
      return;
    }

    setFile(picked);

    // Read the first 64 KB for header preview.
    const slice = picked.slice(0, HEADER_PREVIEW_BYTES);
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const p = buildHeaderPreview(text);
      if (!p || p.headers.length === 0) {
        setError('Could not read headers from the first part of the file.');
        return;
      }
      setPreview(p);
      // Default mapping: every column starts as 'import'.
      const m: Record<string, 'import' | 'ignore'> = {};
      for (let i = 0; i < p.headers.length; i++) {
        const h = p.headers[i];
        // Empty headers default to 'ignore' since they can't be imported.
        m[h] = h.trim() === '' ? 'ignore' : 'import';
      }
      setMapping(m);
      setStage('preview');
    };
    reader.onerror = () => setError('Could not read the selected file.');
    reader.readAsText(slice);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFilePicked(e.dataTransfer.files[0]);
    }
  }

  async function startUpload() {
    if (!file || !preview) {return;}
    // Filter out duplicate-header keys: keep only the last index's
    // selection, which matches what the backend will see (csv-parse's
    // columns: true keeps the last occurrence). Drop empty-string
    // header keys.
    const cleanMapping: Record<string, 'import' | 'ignore'> = {};
    for (const h of preview.headers) {
      if (h.trim() === '') {continue;}
      cleanMapping[h] = mapping[h] || 'ignore';
    }
    if (!Object.values(cleanMapping).some(v => v === 'import')) {
      setError('Pick at least one column to Import.');
      return;
    }

    setError(null);
    setStage('upload');
    setUploadPct(0);
    try {
      const { jobId } = await api.uploadCsv(
        file,
        dataBodyId,
        cleanMapping,
        (loaded, total) => setUploadPct(total ? Math.round((loaded / total) * 100) : 0)
      );
      setJobId(jobId);
      setStage('progress');
      schedulePoll(jobId);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err.message || 'Upload failed';
      setError(msg);
      setStage('preview');
    }
  }

  function schedulePoll(id: string) {
    if (pollTimer.current) {clearTimeout(pollTimer.current);}
    pollTimer.current = setTimeout(async () => {
      try {
        const s = await api.getCsvImportStatus(id);
        setStatus(s);
        if (s.status === 'completed' || s.status === 'failed' || s.status === 'canceled') {
          setStage('summary');
          return;
        }
      } catch {
        // network blip; keep polling
      }
      schedulePoll(id);
    }, 1500);
  }

  // Stop polling on unmount.
  useEffect(() => () => {
    if (pollTimer.current) {clearTimeout(pollTimer.current);}
  }, []);

  // -------------------------------------------------------------------
  // Import-in-progress guards.
  // -------------------------------------------------------------------
  // Two danger profiles:
  //   - Phase 1 ("upload"): browser is streaming the file to the server.
  //     A tab close / refresh / navigation HERE aborts the POST and the
  //     server never receives the file — the user loses everything.
  //   - Phase 2 ("progress"): server has the file and a background worker
  //     is processing it. Closing the modal or the tab here is safe; the
  //     worker keeps running. But users complained that dismissing the
  //     dialog by clicking outside makes it disappear with no easy way to
  //     get progress back — so we treat it as "active but recoverable":
  //     can't dismiss via click-outside, X button shows a confirmation
  //     that explains the import is still running in the background.
  const isActiveImport = stage === 'upload' || stage === 'progress';
  const isDangerousPhase = stage === 'upload';

  // Browser-level guard for Phase 1 only. Most browsers will ignore the
  // `returnValue` text and show a generic "leave site?" prompt, but the
  // confirm dialog still appears, which is all we need.
  useEffect(() => {
    if (!isDangerousPhase) {return;}
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'An import is still uploading. If you leave now, the upload will be aborted and the server will never receive the file.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDangerousPhase]);

  // X-button close — gated by a confirm during an active import. Two
  // different messages by phase so the user knows exactly what risk
  // they're accepting.
  function attemptClose() {
    if (!isActiveImport) {
      onClose();
      return;
    }
    const msg = isDangerousPhase
      ? 'The CSV is still uploading to the server. Closing now will ABORT the upload — no leads will be imported and you\'ll have to start over.\n\nClose anyway?'
      : 'The import is running in the background on the server. Closing this dialog will not stop the import — your leads will continue to load — but you\'ll lose this progress view.\n\nClose anyway?';
    if (window.confirm(msg)) {
      onClose();
    }
  }

  // Overlay click — total no-op during an active import. The user has to
  // physically click the X to leave, and that hits attemptClose() above.
  function handleOverlayClick() {
    if (isActiveImport) {return;}
    onClose();
  }

  async function handleCancel() {
    if (!jobId || canceling) {return;}
    setCanceling(true);
    try {
      await api.cancelCsvImportJob(jobId);
    } catch {
      // ignore — worker will catch up
    }
    setCanceling(false);
  }

  async function handleDownloadRejected() {
    if (!jobId || !status) {return;}
    const base = (status.filename || 'import').replace(/\.csv$/i, '');
    try {
      await api.downloadCsvImportRejected(jobId, `${base}_rejected.csv`);
    } catch (err: any) {
      setError('Could not download rejected rows: ' + (err?.response?.data?.error || err.message));
    }
  }

  // Counters used in the progress bar
  const progressFrac = useMemo(() => {
    if (!status) {return 0;}
    if (!status.totalRows) {
      // Total isn't known until EOF — just show ratio of processed to fileSize-implied estimate.
      // We don't know rows-per-byte, so just animate gently.
      return Math.min(0.95, status.processedRows / Math.max(1, status.processedRows + 5000));
    }
    return Math.min(1, status.processedRows / status.totalRows);
  }, [status]);

  if (!open) {return null;}

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          maxWidth: '92vw',
          width: 760,
          maxHeight: '88vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Upload Leads from CSV</h2>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Importing into <strong style={{ color: '#374151' }}>{dataBodyName}</strong>
              {isDangerousPhase && (
                <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 4, background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600 }}>
                  Do not close — upload in progress
                </span>
              )}
              {!isDangerousPhase && stage === 'progress' && (
                <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontSize: 11, fontWeight: 600 }}>
                  Import running in background
                </span>
              )}
            </div>
          </div>
          <button
            onClick={attemptClose}
            style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#9ca3af', padding: 0, width: 32, height: 32, lineHeight: 1 }}
            aria-label="Close"
            title={isActiveImport ? 'Click to leave (you will see a warning)' : 'Close'}
          >&times;</button>
        </div>

        {/* Body */}
        <div style={{ overflow: 'auto', flex: 1, padding: 24 }}>

          {/* ---------- STAGE 1: RULES + DROPZONE ---------- */}
          {stage === 'rules' && (
            <>
              <div style={{ background: '#f8f9fb', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: '#111827' }}>Before you upload</div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Only <strong>.csv</strong> files are accepted.</li>
                  <li>The first row must be a <strong>header row</strong>.</li>
                  <li>Maximum file size: <strong>{limits ? fmtBytes(limits.maxUploadBytes) : '—'}</strong>.</li>
                  <li>You'll preview the headers next and choose which columns to <strong>Import</strong> or <strong>Ignore</strong>.</li>
                  <li>Rows that fail (malformed, missing required fields, etc.) are skipped and saved to a downloadable rejected-rows file. The rest of the import keeps going.</li>
                  <li>If we hit <strong>{limits?.blankRowGapStop ?? 50} blank rows in a row</strong>, we treat that as the end of the file and stop. (Some CSVs have deleted rows that leave gaps — this avoids waiting forever.)</li>
                </ul>
              </div>

              <div
                onDrop={onDrop}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragOver ? '#6366f1' : '#d1d5db'}`,
                  borderRadius: 12,
                  padding: '40px 20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: dragOver ? '#eef2ff' : '#fafbfc',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontSize: 40, marginBottom: 8 }}>+</div>
                <div style={{ fontWeight: 600, color: '#111827', fontSize: 15, marginBottom: 4 }}>
                  Drop a .csv file here, or click to choose one
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>
                  We'll show you the headers first. Nothing gets imported until you confirm.
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {handleFilePicked(e.target.files[0]);}
                    // reset so the same file can be picked again after a cancel
                    e.target.value = '';
                  }}
                />
              </div>

              {error && (
                <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 8, fontSize: 13 }}>
                  {error}
                </div>
              )}
            </>
          )}

          {/* ---------- STAGE 2: HEADER PREVIEW + MAPPING ---------- */}
          {stage === 'preview' && file && preview && (
            <>
              <div style={{ marginBottom: 12, fontSize: 13, color: '#6b7280' }}>
                <strong style={{ color: '#111827' }}>{file.name}</strong> &middot; {fmtBytes(file.size)} &middot; {preview.headers.length} columns detected
              </div>

              {preview.warnings.length > 0 && (
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#92400e' }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Heads up</div>
                  <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                    {preview.warnings.map((w, i) => <li key={i}>{w.text}</li>)}
                  </ul>
                </div>
              )}

              <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 8 }}>
                Choose what to import
              </div>

              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8f9fb' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Column</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sample</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', width: 200 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.headers.map((h, idx) => {
                      const sample = preview.sampleRows[0]?.[idx] ?? '';
                      const isEmpty = h.trim() === '';
                      const action = mapping[h] || (isEmpty ? 'ignore' : 'import');
                      return (
                        <tr key={idx} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 500, color: '#111827', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={h}>
                            {isEmpty ? <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>(blank)</span> : h}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#6b7280', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sample}>
                            {sample || <span style={{ color: '#d1d5db' }}>&mdash;</span>}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #d1d5db' }}>
                              <button
                                disabled={isEmpty}
                                onClick={() => setMapping(m => ({ ...m, [h]: 'import' }))}
                                style={{
                                  padding: '4px 12px',
                                  border: 'none',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: isEmpty ? 'not-allowed' : 'pointer',
                                  background: action === 'import' ? '#6366f1' : 'transparent',
                                  color: action === 'import' ? 'white' : (isEmpty ? '#d1d5db' : '#374151'),
                                }}
                              >Import</button>
                              <button
                                onClick={() => setMapping(m => ({ ...m, [h]: 'ignore' }))}
                                style={{
                                  padding: '4px 12px',
                                  border: 'none',
                                  borderLeft: '1px solid #d1d5db',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  background: action === 'ignore' ? '#9ca3af' : 'transparent',
                                  color: action === 'ignore' ? 'white' : '#374151',
                                }}
                              >Ignore</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {error && (
                <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 8, fontSize: 13 }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setFile(null); setPreview(null); setMapping({}); setStage('rules'); }}
                >
                  Choose a different file
                </button>
                <button
                  className="btn btn-primary"
                  onClick={startUpload}
                >
                  Start import
                </button>
              </div>
            </>
          )}

          {/* ---------- STAGE 3: NETWORK UPLOAD ---------- */}
          {stage === 'upload' && (
            <div style={{ padding: '40px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Uploading {file?.name}…</div>
              <div style={{ width: '100%', height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${uploadPct}%`, height: '100%', background: '#6366f1', transition: 'width 0.2s' }} />
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>{uploadPct}%</div>
            </div>
          )}

          {/* ---------- STAGE 4: SERVER PROCESSING ---------- */}
          {stage === 'progress' && status && (
            <>
              <div style={{ marginBottom: 16, fontSize: 13, color: '#6b7280' }}>
                <strong style={{ color: '#111827' }}>{status.filename}</strong> — server is importing rows.
              </div>

              <div style={{ width: '100%', height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ width: `${Math.round(progressFrac * 100)}%`, height: '100%', background: '#6366f1', transition: 'width 0.4s' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
                <Stat label="Rows seen" value={fmtNum(status.processedRows)} />
                <Stat label="Imported" value={fmtNum(status.succeededRows)} good />
                <Stat label="Rejected" value={fmtNum(status.failedRows)} bad={status.failedRows > 0} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={canceling}
                >
                  {canceling ? 'Canceling…' : 'Cancel import'}
                </button>
              </div>
            </>
          )}

          {/* ---------- STAGE 5: SUMMARY ---------- */}
          {stage === 'summary' && status && (
            <>
              <div style={{ marginBottom: 16, fontSize: 13, color: '#6b7280' }}>
                <strong style={{ color: '#111827' }}>{status.filename}</strong> — {labelForFinalStatus(status.status)}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <Stat label="Rows seen" value={fmtNum(status.processedRows)} />
                <Stat label="Imported" value={fmtNum(status.succeededRows)} good />
                <Stat label="Rejected" value={fmtNum(status.failedRows)} bad={status.failedRows > 0} />
              </div>

              {status.gapStopAtRow != null && (
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: 12, fontSize: 13, color: '#92400e', marginBottom: 12 }}>
                  Stopped at row <strong>{fmtNum(status.gapStopAtRow)}</strong> after {limits?.blankRowGapStop ?? 50} consecutive blank rows. Anything after that gap was not imported.
                </div>
              )}

              {status.error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, fontSize: 13, color: '#991b1b', marginBottom: 12 }}>
                  {status.error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                <div>
                  {status.hasRejectedFile && (
                    <button className="btn btn-secondary" onClick={handleDownloadRejected}>
                      Download rejected rows
                    </button>
                  )}
                </div>
                <button className="btn btn-primary" onClick={attemptClose}>Close</button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

function labelForFinalStatus(s: api.CsvImportStatus['status']): string {
  if (s === 'completed') {return 'import complete.';}
  if (s === 'canceled')  {return 'import canceled.';}
  if (s === 'failed')    {return 'import failed.';}
  return s;
}

function Stat({ label, value, good, bad }: { label: string; value: string; good?: boolean; bad?: boolean }) {
  const color = good ? '#166534' : bad ? '#991b1b' : '#111827';
  const bg = good ? '#dcfce7' : bad ? '#fee2e2' : '#f8f9fb';
  return (
    <div style={{ background: bg, borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}
